import { useParams, Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Calendar, Volume2, VolumeX, Pause, Play, Share2, X, RotateCcw } from "lucide-react";

// Social Share Icons
const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Enhanced Text-to-Speech with Female Voice Preference
function useTextToSpeech(text) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isSupported, setIsSupported] = useState(false);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [selectedVoice, setSelectedVoice] = useState(null);
  
  const chunksRef = useRef([]);
  const progressInterval = useRef(null);
  const isPlayingRef = useRef(false);
  const startTimeRef = useRef(0);

  // Find the best female voice
  const findBestVoice = useCallback(() => {
    const synth = window.speechSynthesis;
    const voices = synth.getVoices();
    
    if (voices.length === 0) return null;
    
    // Priority list of preferred female voices (natural sounding)
    const femaleVoicePreferences = [
      // iOS/macOS - High quality female voices
      'Samantha', // Default iOS/macOS female
      'Karen', // Australian English
      'Moira', // Irish English
      'Fiona', // Scottish English
      'Victoria', // US English
      'Allison', // US English
      // Android/Google - Female voices
      'Google US English Female',
      'Google UK English Female',
      'Female',
      // Microsoft - Female voices
      'Microsoft Zira',
      'Zira',
      'Microsoft Eva',
      'Eva',
      'Aria',
      'Jenny',
      // Generic fallbacks
      'female',
      'woman',
    ];

    // Try to find a preferred female voice
    for (const pref of femaleVoicePreferences) {
      const voice = voices.find(v => 
        v.name.toLowerCase().includes(pref.toLowerCase()) && 
        v.lang.startsWith('en')
      );
      if (voice) {
        console.log('[TTS] Selected voice:', voice.name);
        return voice;
      }
    }

    // Fallback: Any English voice that sounds female (heuristic)
    const englishVoices = voices.filter(v => v.lang.startsWith('en'));
    const likelyFemale = englishVoices.find(v => 
      !v.name.toLowerCase().includes('male') &&
      !v.name.toLowerCase().includes('david') &&
      !v.name.toLowerCase().includes('james') &&
      !v.name.toLowerCase().includes('daniel') &&
      !v.name.toLowerCase().includes('alex') &&
      !v.name.toLowerCase().includes('mark')
    );
    
    if (likelyFemale) {
      console.log('[TTS] Fallback voice:', likelyFemale.name);
      return likelyFemale;
    }

    // Last resort: first English voice
    return englishVoices[0] || voices[0];
  }, []);

  // Load voices
  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth) {
      setIsSupported(false);
      return;
    }
    
    setIsSupported(true);

    const loadVoices = () => {
      const voices = synth.getVoices();
      if (voices.length > 0) {
        const best = findBestVoice();
        setSelectedVoice(best);
      }
    };

    // Load immediately and on change
    loadVoices();
    synth.onvoiceschanged = loadVoices;

    return () => {
      synth.cancel();
      if (progressInterval.current) clearInterval(progressInterval.current);
    };
  }, [findBestVoice]);

  // Clean and split text into chunks
  const prepareText = useCallback((rawText) => {
    if (!rawText) return [];
    
    // Clean markdown and improve readability for speech
    let cleaned = rawText
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/#{1,6}\s/g, '')
      .replace(/[-*]\s/g, '')
      .replace(/\n{2,}/g, '. ')
      .replace(/\n/g, ' ')
      .replace(/\s+/g, ' ')
      // Improve medical term pronunciation
      .replace(/mg\b/g, ' milligrams')
      .replace(/mL\b/gi, ' milliliters')
      .replace(/mcg\b/gi, ' micrograms')
      .replace(/IV\b/g, ' intravenous')
      .replace(/IM\b/g, ' intramuscular')
      .trim();

    // Split into natural sentences
    const sentences = cleaned.match(/[^.!?]+[.!?]+/g) || [cleaned];
    
    // Group into chunks of ~150 characters for smoother delivery
    const chunks = [];
    let currentChunk = '';
    
    for (const sentence of sentences) {
      if ((currentChunk + sentence).length > 150 && currentChunk.length > 0) {
        chunks.push(currentChunk.trim());
        currentChunk = sentence;
      } else {
        currentChunk += ' ' + sentence;
      }
    }
    
    if (currentChunk.trim()) {
      chunks.push(currentChunk.trim());
    }

    return chunks.length > 0 ? chunks : [cleaned];
  }, []);

  // Speak a single chunk with smooth transitions
  const speakChunk = useCallback((chunkIndex) => {
    const synth = window.speechSynthesis;
    const chunks = chunksRef.current;
    
    if (chunkIndex >= chunks.length || !isPlayingRef.current) {
      // Finished all chunks
      setIsPlaying(false);
      setIsPaused(false);
      setProgress(100);
      isPlayingRef.current = false;
      if (progressInterval.current) clearInterval(progressInterval.current);
      setTimeout(() => setProgress(0), 2000);
      return;
    }

    setCurrentChunk(chunkIndex);
    
    const utterance = new SpeechSynthesisUtterance(chunks[chunkIndex]);
    
    // Configure for natural, professional sound
    utterance.rate = 0.92; // Slightly slower for clarity
    utterance.pitch = 1.05; // Slightly higher for pleasant tone
    utterance.volume = 1.0;

    // Use selected female voice
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    utterance.onend = () => {
      if (isPlayingRef.current) {
        // Small pause between chunks for natural flow
        setTimeout(() => speakChunk(chunkIndex + 1), 150);
      }
    };

    utterance.onerror = (event) => {
      console.warn('[TTS] Chunk error:', event.error);
      // Continue to next chunk on error (don't stop completely)
      if (isPlayingRef.current && event.error !== 'canceled') {
        setTimeout(() => speakChunk(chunkIndex + 1), 100);
      }
    };

    synth.speak(utterance);
  }, [selectedVoice]);

  // Start playing
  const play = useCallback(() => {
    if (!isSupported || !text) return;
    
    const synth = window.speechSynthesis;
    synth.cancel();
    
    const chunks = prepareText(text);
    chunksRef.current = chunks;
    setTotalChunks(chunks.length);
    setCurrentChunk(0);
    
    console.log('[TTS] Starting playback with', chunks.length, 'chunks');
    
    setIsPlaying(true);
    setIsPaused(false);
    isPlayingRef.current = true;
    startTimeRef.current = Date.now();
    
    // Progress tracking
    if (progressInterval.current) clearInterval(progressInterval.current);
    const estimatedDuration = text.length * 50;
    
    progressInterval.current = setInterval(() => {
      if (!isPlayingRef.current) {
        clearInterval(progressInterval.current);
        return;
      }
      const elapsed = Date.now() - startTimeRef.current;
      const prog = Math.min((elapsed / estimatedDuration) * 100, 99);
      setProgress(prog);
    }, 150);

    speakChunk(0);
  }, [text, isSupported, prepareText, speakChunk]);

  // Pause
  const pause = useCallback(() => {
    if (!isPlaying || isPaused) return;
    window.speechSynthesis.pause();
    setIsPaused(true);
    if (progressInterval.current) clearInterval(progressInterval.current);
  }, [isPlaying, isPaused]);

  // Resume
  const resume = useCallback(() => {
    if (!isPaused) return;
    
    const synth = window.speechSynthesis;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      // iOS doesn't support resume well - restart from current chunk
      synth.cancel();
      setIsPaused(false);
      isPlayingRef.current = true;
      startTimeRef.current = Date.now() - (progress / 100) * (text.length * 50);
      
      progressInterval.current = setInterval(() => {
        if (!isPlayingRef.current) {
          clearInterval(progressInterval.current);
          return;
        }
        const elapsed = Date.now() - startTimeRef.current;
        const prog = Math.min((elapsed / (text.length * 50)) * 100, 99);
        setProgress(prog);
      }, 150);
      
      speakChunk(currentChunk);
    } else {
      synth.resume();
      setIsPaused(false);
    }
  }, [isPaused, currentChunk, speakChunk, progress, text]);

  // Stop
  const stop = useCallback(() => {
    window.speechSynthesis.cancel();
    isPlayingRef.current = false;
    setIsPlaying(false);
    setIsPaused(false);
    setProgress(0);
    setCurrentChunk(0);
    if (progressInterval.current) clearInterval(progressInterval.current);
  }, []);

  // Restart
  const restart = useCallback(() => {
    stop();
    setTimeout(() => play(), 100);
  }, [stop, play]);

  return { 
    isPlaying, 
    isPaused, 
    progress, 
    isSupported,
    currentChunk,
    totalChunks,
    voiceName: selectedVoice?.name || 'Default',
    play, 
    pause, 
    resume, 
    stop,
    restart
  };
}

// Share Modal Component
function ShareModal({ isOpen, onClose, title, url }) {
  if (!isOpen) return null;

  const shareUrl = encodeURIComponent(url);
  const shareTitle = encodeURIComponent(title);
  const shareText = encodeURIComponent(`${title} - Read more from Drukst Biotech`);

  const shareLinks = [
    {
      name: 'WhatsApp',
      icon: <WhatsAppIcon />,
      url: `https://wa.me/?text=${shareText}%20${shareUrl}`,
      color: 'hover:bg-green-500/20 hover:text-green-400',
    },
    {
      name: 'LinkedIn',
      icon: <LinkedInIcon />,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
      color: 'hover:bg-blue-500/20 hover:text-blue-400',
    },
    {
      name: 'X (Twitter)',
      icon: <TwitterIcon />,
      url: `https://twitter.com/intent/tweet?text=${shareTitle}&url=${shareUrl}`,
      color: 'hover:bg-white/20 hover:text-white',
    },
  ];

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      const btn = document.getElementById('copy-btn');
      if (btn) {
        btn.textContent = 'Copied!';
        setTimeout(() => btn.textContent = 'Copy Link', 2000);
      }
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-[#0F172A] rounded-xl border border-white/10 p-5 w-full max-w-xs"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-bold text-white">Share Article</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-[#94A3B8] hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="flex justify-center gap-3 mb-5">
          {shareLinks.map((link) => (
            <a
              key={link.name}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`p-2.5 rounded-lg bg-white/5 text-[#94A3B8] transition-all ${link.color}`}
              title={`Share on ${link.name}`}
            >
              {link.icon}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-white/5 border border-white/5">
          <input
            type="text"
            value={url}
            readOnly
            className="flex-1 bg-transparent text-[#94A3B8] text-xs outline-none truncate"
          />
          <button
            id="copy-btn"
            onClick={copyToClipboard}
            className="px-2.5 py-1 rounded bg-[#2DD4BF]/10 text-[#2DD4BF] text-[10px] font-semibold hover:bg-[#2DD4BF]/20 transition-colors whitespace-nowrap"
          >
            Copy Link
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function BlogPost() {
  const { id } = useParams();
  const location = useLocation();
  const [post, setPost] = useState(location.state?.post || null);
  const [showShare, setShowShare] = useState(false);
  
  const tts = useTextToSpeech(post?.content || '');

  useEffect(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices();
    }
  }, []);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="post-not-found">
        <div className="text-center">
          <h2 className="text-xl font-bold text-white mb-3">Post Not Found</h2>
          <Link to="/blog" className="text-[#2DD4BF] text-sm hover:underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  const currentUrl = typeof window !== 'undefined' ? window.location.href : `https://drukstbiotech.com/blog/${id}`;

  return (
    <div data-testid="blog-post-page" className="min-h-screen pt-16 md:pt-0">
      {/* Back */}
      <div className="max-w-3xl mx-auto px-6 pt-24 md:pt-28">
        <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
          <Link
            to="/blog"
            data-testid="back-to-blog"
            className="inline-flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-[#2DD4BF] transition-colors mb-8"
          >
            <ArrowLeft size={14} /> Back
          </Link>
        </motion.div>
      </div>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-6 pb-16">
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-[#2DD4BF]/10 text-[#2DD4BF]">
              {post.category}
            </span>
            <span className="text-[11px] text-[#94A3B8] flex items-center gap-1">
              <Calendar size={11} />
              {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
            </span>
            <span className="text-[11px] text-[#94A3B8] flex items-center gap-1">
              <Clock size={11} /> {post.readTime}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-extrabold text-white leading-tight mb-5" data-testid="blog-post-title">
            {post.title}
          </h1>

          {/* Audio Controls */}
          {tts.isSupported && (
            <div className="flex flex-wrap items-center gap-2 mb-6 p-3 rounded-lg bg-white/[0.02] border border-white/5">
              {!tts.isPlaying ? (
                <button
                  onClick={tts.play}
                  data-testid="listen-blog-btn"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#2DD4BF] text-[#022C22] text-xs font-semibold hover:bg-[#2DD4BF]/90 transition-colors active:scale-95"
                >
                  <Volume2 size={14} />
                  <span>Listen to Article</span>
                </button>
              ) : (
                <>
                  <button
                    onClick={tts.isPaused ? tts.resume : tts.pause}
                    data-testid="tts-pause-resume"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#2DD4BF] text-[#022C22] text-xs font-semibold transition-colors active:scale-95"
                  >
                    {tts.isPaused ? <Play size={13} /> : <Pause size={13} />}
                    <span>{tts.isPaused ? 'Resume' : 'Pause'}</span>
                  </button>
                  <button
                    onClick={tts.restart}
                    className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors active:scale-95"
                    title="Restart"
                  >
                    <RotateCcw size={13} />
                  </button>
                  <button
                    onClick={tts.stop}
                    className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors active:scale-95"
                    title="Stop"
                  >
                    <VolumeX size={13} />
                  </button>
                  {/* Progress */}
                  <div className="flex items-center gap-2 ml-auto">
                    <div className="w-20 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <motion.div 
                        className="h-full bg-[#2DD4BF]"
                        style={{ width: `${tts.progress}%` }}
                        transition={{ duration: 0.15 }}
                      />
                    </div>
                    <span className="text-[10px] text-[#94A3B8] min-w-[28px]">
                      {Math.round(tts.progress)}%
                    </span>
                  </div>
                </>
              )}
              
              {/* Share button */}
              <button
                onClick={() => setShowShare(true)}
                data-testid="share-blog-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/5 border border-white/10 text-[#94A3B8] text-xs font-medium hover:bg-white/10 hover:text-white transition-colors active:scale-95 ml-auto"
              >
                <Share2 size={13} />
                <span>Share</span>
              </button>
            </div>
          )}

          {/* Featured image */}
          {post.image && (
            <div className="aspect-[2/1] rounded-lg overflow-hidden border border-white/5 mb-8">
              <img src={post.image} alt={post.title} className="w-full h-full object-cover opacity-80" loading="lazy" />
            </div>
          )}

          {/* Content */}
          <div data-testid="blog-post-content" className="prose-content">
            {post.content.split('\n\n').map((paragraph, i) => {
              if (paragraph.trim().startsWith('- ') || paragraph.trim().startsWith('* ')) {
                const items = paragraph.split('\n').filter(l => l.trim());
                return (
                  <ul key={i} className="space-y-1.5 my-5 pl-4">
                    {items.map((item, j) => (
                      <li key={j} className="text-[#94A3B8] text-sm leading-relaxed flex items-start gap-2">
                        <span className="w-1 h-1 rounded-full bg-[#2DD4BF] mt-2 shrink-0" />
                        <span>{item.replace(/^[-*]\s*/, '')}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              return (
                <p key={i} className="text-[#94A3B8] text-sm leading-[1.8] mb-4">
                  {paragraph}
                </p>
              );
            })}
          </div>

          {/* Disclaimer */}
          <div className="mt-10 p-3 rounded-lg bg-white/[0.02] border border-white/5">
            <p className="text-[10px] text-[#94A3B8]/60">
              For healthcare professionals only. Refer to prescribing information for treatment decisions.
            </p>
          </div>
        </motion.div>
      </article>

      {/* Back CTA */}
      <section className="border-t border-white/5 py-12 px-6 pb-28">
        <div className="max-w-3xl mx-auto text-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2DD4BF] text-[#022C22] text-sm font-bold hover:scale-105 transition-transform active:scale-95"
          >
            Back to Blog
          </Link>
        </div>
      </section>

      <ShareModal
        isOpen={showShare}
        onClose={() => setShowShare(false)}
        title={post.title}
        url={currentUrl}
      />
    </div>
  );
}
